import { Observable, throwError } from 'rxjs';
import { switchMap } from 'rxjs';
import { HttpService } from '@app/@core/services/http.service';
import { AttachmentConfig } from '@app/@core/config/attachment-config';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { v4 as uuidv4 } from 'uuid';

const UPLOAD_KEY_ROUTE = AttachmentConfig.EndPoint.Attachments.UploadKey;

/** Blob key segment under DOCUMENTS/ (server adds DOCUMENTS/ prefix in FilesService). */
export function buildEducatorDocumentBlobFileName(originalName: string): string {
  const safe = String(originalName || 'file').replace(/[/\\?#]+/g, '_');
  return `${uuidv4()}/${safe}`;
}

/** Unwrap POST /common/files/upload-key response to the SAS URL string. */
export function extractBlobUploadUrlFromUploadKeyResponse(res: unknown): string | null {
  if (res == null) {
    return null;
  }
  if (typeof res === 'string' && res.trim()) {
    return res.trim();
  }
  const r = res as Record<string, unknown>;
  if (typeof r.data === 'string' && (r.data as string).trim()) {
    return (r.data as string).trim();
  }
  if (r.data != null && typeof r.data === 'object') {
    const d = r.data as Record<string, unknown>;
    if (typeof d.data === 'string' && (d.data as string).trim()) {
      return String(d.data).trim();
    }
    if (typeof d.path === 'string' && d.path.trim()) {
      return d.path.trim();
    }
  }
  if (typeof r.path === 'string' && r.path.trim()) {
    return r.path.trim();
  }
  return null;
}

/**
 * Match document-upload-grid: get SAS PUT URL, PUT bytes to blob, then persist row with that same URL as file_hash.
 */
export function educatorDocumentUploadWithClientBlobPut(
  http: HttpService,
  file: File,
  uploadData: Record<string, unknown>
): Observable<unknown> {
  const fileName = buildEducatorDocumentBlobFileName(file.name);
  const contentType = file.type || 'application/octet-stream';
  return http.post<unknown>(UPLOAD_KEY_ROUTE, { fileName, contentType }).pipe(
    switchMap((keyRes) => {
      const url = extractBlobUploadUrlFromUploadKeyResponse(keyRes);
      if (!url) {
        return throwError(() => new Error('Could not obtain blob upload URL'));
      }
      return http.putUpload(url, file, contentType).pipe(
        switchMap(() =>
          http.post<unknown>(API_ENDPOINTS.EDUCATOR_DOCUMENT_UPLOAD, {
            uploadData,
            filePath: url
          })
        )
      );
    })
  );
}

/** Same two-step flow for /search/educator-communication-upload (notes with attachment). */
export function educatorCommunicationUploadWithClientBlobPut(
  http: HttpService,
  file: File,
  uploadData: Record<string, unknown>
): Observable<unknown> {
  const fileName = buildEducatorDocumentBlobFileName(file.name);
  const contentType = file.type || 'application/octet-stream';
  return http.post<unknown>(UPLOAD_KEY_ROUTE, { fileName, contentType }).pipe(
    switchMap((keyRes) => {
      const url = extractBlobUploadUrlFromUploadKeyResponse(keyRes);
      if (!url) {
        return throwError(() => new Error('Could not obtain blob upload URL'));
      }
      return http.putUpload(url, file, contentType).pipe(
        switchMap(() =>
          http.post<unknown>(API_ENDPOINTS.Profile_Notes_Upload, {
            uploadData,
            filePath: url
          })
        )
      );
    })
  );
}
